/**
 * Number-input guards.
 *
 * A focused `<input type="number">` steps its value on every mouse-wheel tick,
 * so a dealer who scrolls the page with the cursor resting on a field they just
 * typed in silently changes it — a price, a quantity, a template amount. The
 * spinner arrows are already hidden in `css/_resets.css`; the wheel is the half
 * that still bites.
 */

/**
 * Register once at boot. Blurring (rather than preventDefault) is what lets the
 * page and grids keep scrolling under the cursor: the value only steps while the
 * input has focus, so dropping focus cancels the step and leaves the scroll.
 *
 * Bubble phase, so inputs that already handle the wheel themselves (the grid
 * price editors use `@wheel.prevent`) are left alone — their step is already
 * cancelled, so there is nothing to guard and no reason to pull focus out of an
 * open cell editor.
 */
export const installNumberInputWheelGuard = () => {
  document.addEventListener('wheel', (event) => {
    if (event.defaultPrevented) return

    const target = event.target

    if (target instanceof HTMLInputElement && target.type === 'number' && document.activeElement === target) {
      target.blur()
    }
  }, { passive: true })
}

// `-` and `+` are signs, `e`/`E` an exponent: all valid in a number input, none
// meaningful in a price.
const NON_PRICE_KEYS = ['-', '+', 'e', 'E']

/** `@keydown` on a price input: stops a sign or exponent being typed at all. */
export const blockNonPriceKeys = (event) => {
  if (NON_PRICE_KEYS.includes(event.key)) event.preventDefault()
}

/**
 * `@input` on a price input: the keydown guard can't see a paste, a drop or
 * autofill, so a negative that arrives that way is clamped to 0 here, before the
 * value reaches a store. Returns the (possibly corrected) value.
 *
 * Inputs bound with `v-model` must also write the return value back to their
 * model: v-model's own listener runs first and has already read the negative.
 */
export const clampNegativePrice = (event) => {
  const input = event.target

  if (input.value !== '' && Number(input.value) < 0) {
    input.value = '0'
  }

  return input.value
}
